import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleResponse } from 'actions/actionHelpers';
import arrayMutators from 'final-form-arrays';
import storageGroupCategories from 'enums/storageGroupCategories';
import { fetchSeoGeoItems, updateSeoGeoItems } from 'actions/seo';

import PageTitle from 'common/PageTitle';
import PasswordProtector from 'common/PasswordProtector';
import ShadowBox from 'common/ShadowBox';
import Loader from 'common/Loader';
import Container from 'common/Container';
import Form from 'form/Form';
import Button from '@material-ui/core/Button';
import ButtonContainer from 'common/ButtonContainer';
import { FieldArray } from 'react-final-form-arrays';
import { OnChange } from 'react-final-form-listeners';
import { TextField } from 'mui-rff';
import MenuItem from '@material-ui/core/MenuItem';
import { createInitialSeoGeoBlocks, getDefaultSeoGeoBlock, serializeSeoGeoContent } from 'helpers/SeoGeoContentHelper';
import SeoGeoItemEditor from './SeoGeoItemEditor';

const SeoGeoItemsEditorPage = () => {
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(true);
    const [initialValues, setInitialValues] = useState();

    const isEmptyBlock = block => {
        if(!block) {
            return true;
        }

        switch(block.type) {
            case 'faq':
                return !Array.isArray(block.faqItems) || block.faqItems.every(item => !item.question && !item.answer);
            case 'cta':
                return !block.label && !block.url;
            case 'image':
                return !block.url && !block.alt && !block.caption;
            case 'text':
            default:
                return !block.text;
        }
    };

    const getNonEmptyBlocks = item => {
        const blocks = item && Array.isArray(item.blocks) ? item.blocks : [];
        return blocks.filter(block => !isEmptyBlock(block));
    };

    const mapItemForForm = item => ({
        ...item,
        blocks: createInitialSeoGeoBlocks(item.content)
    });

    const mapItemForSubmit = item => {
        const blocks = getNonEmptyBlocks(item);

        return {
            location: item.location,
            h1: item.h1,
            title: item.title,
            metaDescription: item.metaDescription,
            content: serializeSeoGeoContent(blocks)
        };
    };

    useEffect(() => {
        fetchData(storageGroupCategories.storeHouse.key);
    }, []);

    const fetchData = storageGroupCategory => {
        setIsLoading(true);
        dispatch(fetchSeoGeoItems(storageGroupCategory))
            .then(handleResponse(
                response => {
                    setInitialValues({
                        storageGroupCategory,
                        items: [ ...response.payload.map(mapItemForForm), createNewItem() ]
                    });
                    setIsLoading(false);
                },
                () => setIsLoading(false)
            ));
    };

    const isEmptyItem = item => !item.location && !item.h1 && !item.title && !item.metaDescription && getNonEmptyBlocks(item).length === 0;

    const createNewItem = () => ({
        location: null,
        h1: null,
        title: null,
        metaDescription: null,
        content: null,
        blocks: [ getDefaultSeoGeoBlock() ]
    });

    const removeEmptyItems = items => items.filter(o => !isEmptyItem(o));

    const handleRemoveItem = (fields, index) => {
        fields.remove(index);
    };

    const handleItemBlur = (fields, index) => {
        if(fields.length - 1 === index && !isEmptyItem(fields.value[index])) {
            fields.push(createNewItem());
        }
    };

    const handleFormSubmit = values => {
        setIsLoading(true);
        setInitialValues(values);
        const items = removeEmptyItems(values.items).map(mapItemForSubmit);
        dispatch(updateSeoGeoItems(values.storageGroupCategory, items))
            .then(handleResponse(
                () => {
                    setIsLoading(false);
                    return { snackbar: { message: 'Saved!' }};
                },
                () => setIsLoading(false)
            ));
    };

    const title = 'Edit SEO geo items';

    return (
        <Container>
            <PageTitle>{title}</PageTitle>
            <PasswordProtector passwordType="seoGeoItemsEditor">
                <ShadowBox title={title}>
                    {
                        isLoading && <Loader />
                    }
                    {
                        // logged in
                        !isLoading &&
                        (
                            <Form
                                initialValues={initialValues}
                                mutators={arrayMutators}
                                onSubmit={handleFormSubmit}
                            >
                                {({ values, handleSubmit, submitting }) => (
                                    <form onSubmit={handleSubmit}>
                                        <TextField
                                            select
                                            label="Storage group category"
                                            name="storageGroupCategory"
                                            variant="outlined"
                                        >
                                            {
                                                Object.values(storageGroupCategories)
                                                    .map(o => (
                                                        <MenuItem key={o.key} value={o.key}>
                                                            {o.title}
                                                        </MenuItem>
                                                    ))
                                            }
                                        </TextField>
                                        <OnChange name="storageGroupCategory">
                                            {() => fetchData(values.storageGroupCategory)}
                                        </OnChange>

                                        <FieldArray name="items">
                                            {({ fields }) => (
                                                fields.map((name, index) => (
                                                    <SeoGeoItemEditor
                                                        key={index}
                                                        name={name}
                                                        onRemoveItem={() => handleRemoveItem(fields, index)}
                                                        onBlur={() => handleItemBlur(fields, index)}
                                                    />
                                                ))
                                            )}
                                        </FieldArray>

                                        <ButtonContainer>
                                            <Button
                                                type="submit"
                                                color="primary"
                                                variant="contained"
                                                disabled={submitting}
                                            >
                                                Save
                                            </Button>
                                        </ButtonContainer>
                                    </form>
                                )}
                            </Form>
                        )
                    }
                </ShadowBox>
            </PasswordProtector>
        </Container>
    );
};

export default SeoGeoItemsEditorPage;
